import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import Logo from '@/components/Logo';
import {
  Radio,
  LayoutGrid,
  Users,
  Sparkles,
  LogOut,
  Settings,
  BarChart2,
  Layers,
  LineChart,
  Shuffle,
  GitMerge,
} from 'lucide-react';

const ChroLayout = ({ children, orgName }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { logout, user } = useAuth();

  // Group 1 — daily screens (always visible)
  const dailyNav = [
    { label: 'Signal Room', icon: Radio, path: '/signal-room' },
    { label: 'Intelligence', icon: BarChart2, path: '/intelligence' },
    { label: 'Blueprint Room', icon: LayoutGrid, path: '/blueprint/cockpit' },
    { label: 'The Stage', icon: Users, path: '/stage' },
    { label: 'Showcase', icon: Sparkles, path: '/showcase/ai-tools' },
  ];

  // Group 2 — product flow (mid-analysis construction steps)
  const flowNav = [
    { label: 'Deconstruct', icon: Layers, path: '/deconstruct' },
    { label: 'Analyze', icon: LineChart, path: '/analyze' },
    { label: 'Sort · 4B', icon: Shuffle, path: '/sort' },
    { label: 'Recompose', icon: GitMerge, path: '/recompose' },
  ];

  const navItems = [...dailyNav, ...flowNav];

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Top Navigation */}
      <header className="h-16 border-b border-border/50 glass-heavy sticky top-0 z-50">
        <div className="max-w-7xl mx-auto h-full px-4 flex items-center justify-between">
          <div className="flex items-center gap-8">
            <Link to="/signal-room">
              <Logo size="nav" />
            </Link>
            
            <nav className="hidden md:flex items-center gap-1">
              {dailyNav.map((item) => {
                const isActive = location.pathname === item.path ||
                  location.pathname.startsWith(item.path + '/');

                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={cn(
                      "flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all",
                      isActive
                        ? "bg-primary/10 text-primary"
                        : "text-muted-foreground hover:text-foreground hover:bg-muted/50"
                    )}
                    data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
                  >
                    <item.icon className="h-4 w-4" />
                    {item.label}
                  </Link>
                );
              })}

              {/* Divider between daily screens and product-flow steps */}
              <span
                aria-hidden="true"
                style={{
                  display: 'inline-block',
                  width: 1,
                  height: 18,
                  background: 'var(--line)',
                  margin: '0 8px',
                }}
              />

              {flowNav.map((item) => {
                const isActive = location.pathname === item.path ||
                  location.pathname.startsWith(item.path + '/');

                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={cn(
                      "flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all",
                      isActive
                        ? "bg-primary/10 text-primary"
                        : "text-muted-foreground hover:text-foreground hover:bg-muted/50"
                    )}
                    data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
                  >
                    <item.icon className="h-4 w-4" />
                    {item.label}
                  </Link>
                );
              })}
            </nav>
          </div>

          <div className="flex items-center gap-4">
            {orgName && (
              <span className="text-sm text-muted-foreground hidden md:block">
                {orgName}
              </span>
            )}
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center">
                <span className="text-sm font-medium text-primary">
                  {user?.name?.charAt(0) || 'C'}
                </span>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleLogout}
                className="text-muted-foreground"
                data-testid="logout-btn"
              >
                <LogOut className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Mobile Navigation */}
      <nav className="md:hidden fixed bottom-0 left-0 right-0 h-16 border-t border-border bg-card/95 backdrop-blur-lg z-50">
        <div className="flex items-center justify-around h-full">
          {navItems.map((item) => {
            const isActive = location.pathname === item.path;
            return (
              <Link
                key={item.path}
                to={item.path}
                className={cn(
                  "flex flex-col items-center gap-1 px-4 py-2",
                  isActive ? "text-primary" : "text-muted-foreground"
                )}
              >
                <item.icon className="h-5 w-5" />
                <span className="text-xs">{item.label.split(' ')[0]}</span>
              </Link>
            );
          })}
        </div>
      </nav>

      {/* Main Content */}
      <main className="pb-20 md:pb-0">
        {children}
      </main>
    </div>
  );
};

export default ChroLayout;
